import { useState, useEffect, useCallback } from 'react'
import {
    Search, Package, Monitor, Smartphone, Globe,
    CheckCircle2, XCircle, ShieldAlert, LogIn, User
} from 'lucide-react'
import { toast } from 'sonner'

// Componentes UI Globales
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { SearchFilter } from '../components/ui/SearchFilter'

// Servicio
import { accessLogService, AccessLog } from '../services/access-log.service'

// ─── Tipos ─────────────────────────────────────────────────────────────────────

interface PaginationState {
    currentPage: number
    total: number
    perPage: number
    lastPage: number
    from: number
    to: number
}

const INITIAL_PAGINATION: PaginationState = {
    currentPage: 1,
    total: 0,
    perPage: 10,
    lastPage: 1,
    from: 0,
    to: 0,
}

// ─── Configuración de tipo de login ──────────────────────────────────────────

interface LoginTypeConfig {
    label: string
    bgColor: string
    textColor: string
    Icon: React.ElementType
}

const LOGIN_TYPE_CONFIG: Record<string, LoginTypeConfig> = {
    //web: { label: 'Web',          bgColor: 'bg-primary/15',   textColor: 'text-primary',   Icon: Globe },
    pin: { label: 'PIN', bgColor: 'bg-violet-100', textColor: 'text-violet-700', Icon: Smartphone },
    password: { label: 'Contraseña', bgColor: 'bg-orange-100', textColor: 'text-orange-700', Icon: User },
    //api: { label: 'API',          bgColor: 'bg-slate-100',  textColor: 'text-slate-700',  Icon: Monitor },
}

const getLoginTypeCfg = (type: string): LoginTypeConfig =>
    LOGIN_TYPE_CONFIG[type?.toLowerCase()] ?? {
        label: type, bgColor: 'bg-slate-100', textColor: 'text-slate-700', Icon: Globe,
    }

// ─── Colores de rol ───────────────────────────────────────────────────────────

const ROLE_PALETTE = [
    'bg-red-100 text-red-700',
    'bg-primary/15 text-primary',
    'bg-emerald-100 text-emerald-700',
    'bg-purple-100 text-purple-700',
    'bg-orange-100 text-orange-700',
    'bg-cyan-100 text-cyan-700',
    'bg-yellow-100 text-yellow-700',
    'bg-slate-100 text-slate-700',
]

function getRoleColor(roleName: string | null): string {
    if (!roleName) return 'bg-slate-100 text-slate-700'
    const n = roleName.toLowerCase()
    if (n.includes('admin')) return ROLE_PALETTE[0]
    if (n.includes('encargado') || n.includes('manager')) return ROLE_PALETTE[1]
    if (n.includes('vendedor') || n.includes('seller')) return ROLE_PALETTE[2]
    if (n.includes('farmacia')) return ROLE_PALETTE[3]

    let hash = 0
    for (let i = 0; i < roleName.length; i++) {
        hash = roleName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return ROLE_PALETTE[Math.abs(hash) % ROLE_PALETTE.length]
}

// ─── Formatear fecha ──────────────────────────────────────────────────────────

function formatDate(raw: string): string {
    try {
        const d = new Date(raw)
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        const yesterday = new Date(now)
        yesterday.setDate(now.getDate() - 1)
        const isYesterday = d.toDateString() === yesterday.toDateString()

        const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

        if (isToday) return `Hoy · ${time}`
        if (isYesterday) return `Ayer · ${time}`

        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
    } catch {
        return raw
    }
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export function LogsAccesos() {
    // ─── Estado ───
    const [logs, setLogs] = useState<AccessLog[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)

    // Filtros
    const [searchQuery, setSearchQuery] = useState('')
    const [filterLoginType, setFilterLoginType] = useState('')
    const [filterSuccess, setFilterSuccess] = useState<string>('')  // '' | 'true' | 'false'
    const [showAllSucursals, setShowAllSucursals] = useState(false)

    // ─── Fetch desde backend ───
    const fetchLogs = useCallback(async (page = 1) => {
        setIsLoading(true)
        try {
            const params: Parameters<typeof accessLogService.getLogs>[0] = {
                page,
                per_page: 10,
                search: searchQuery || undefined,
                login_type: filterLoginType || undefined,
                show_all_sucursals: showAllSucursals,
            }

            if (filterSuccess === 'true') params.success = true
            if (filterSuccess === 'false') params.success = false

            const res = await accessLogService.getLogs(params)

            const paginator = res?.data

            if (paginator?.data) {
                setLogs(paginator.data)
                setPagination({
                    currentPage: paginator.current_page,
                    total: paginator.total,
                    perPage: paginator.per_page,
                    lastPage: paginator.last_page,
                    from: paginator.from ?? 0,
                    to: paginator.to ?? 0,
                })
            } else {
                setLogs([])
                setPagination(INITIAL_PAGINATION)
            }
        } catch {
            toast.error('Error al cargar los registros de acceso')
        } finally {
            setIsLoading(false)
        }
    }, [searchQuery, filterLoginType, filterSuccess, showAllSucursals])

    useEffect(() => {
        fetchLogs(1)
    }, [fetchLogs])

    const handlePageChange = (page: number) => {
        fetchLogs(page)
    }

    // ─── Renderizado ──────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Registro de Accesos</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Historial de inicios y cierres de sesión de los usuarios del sistema.
                    </p>
                </div>

                {/* Filtros */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Búsqueda */}
                    <div className="relative w-56">
                        <SearchFilter
                            placeholder="Buscar usuario, sucursal..."
                            onSearch={(text) => setSearchQuery(text)}
                        />
                    </div>

                    {/* Tipo de acceso */}
                    <select
                        value={filterLoginType}
                        onChange={(e) => setFilterLoginType(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Todos los tipos</option>
                        {/*<option value="web">Web</option>*/}
                        <option value="pin">PIN</option>
                        <option value="password">Contraseña</option>
                        {/*<option value="api">API</option>*/}
                    </select>

                    {/* Resultado */}
                    <select
                        value={filterSuccess}
                        onChange={(e) => setFilterSuccess(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Todos los resultados</option>
                        <option value="true">Exitosos</option>
                        <option value="false">Fallidos</option>
                    </select>

                    {/* Todas las sucursales */}
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                            type="checkbox"
                            id="showAllAccesos"
                            checked={showAllSucursals}
                            onChange={(e) => setShowAllSucursals(e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-blue-500"
                        />
                        Todas las sucursales
                    </label>
                </div>
            </div>

            {/* Alerta de accesos fallidos */}
            {!isLoading && logs.some(l => l.success === false) && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" />
                    <span>
                        Se detectaron <strong>{logs.filter(l => l.success === false).length}</strong> intentos de acceso fallidos en esta página.
                    </span>
                </div>
            )}

            {/* Tabla */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Usuario', 'Rol', 'Sucursal', 'Dispositivo', 'Tipo', 'Resultado', 'Fecha']}
                    columns={['text', 'badge', 'text', 'text', 'badge', 'badge', 'text']}
                    rows={8}
                />
            ) : (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Usuario</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Rol</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Sucursal</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Dispositivo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Resultado</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Fecha</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {logs.length > 0 ? (
                                    logs.map((log, index) => {
                                        const loginCfg = getLoginTypeCfg(log.login_type)
                                        const isSuccess = log.success !== false  // true o undefined → exitoso

                                        return (
                                            <tr
                                                key={index}
                                                className={`transition-colors hover:bg-slate-50 ${!isSuccess ? 'bg-red-50/30' : ''}`}
                                            >
                                                {/* Usuario */}
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-slate-900">{log.user_name || 'Desconocido'}</p>
                                                    <p className="text-xs text-slate-400">{log.user_email}</p>
                                                </td>

                                                {/* Rol */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(log.user_role)}`}>
                                                        {log.user_role || 'Sin rol'}
                                                    </span>
                                                </td>

                                                {/* Sucursal */}
                                                <td className="px-4 py-3 text-sm text-slate-600">
                                                    {log.sucursal_name || <span className="text-slate-300">—</span>}
                                                </td>

                                                {/* Dispositivo */}
                                                <td className="px-4 py-3 text-sm text-slate-600">
                                                    {log.device_name || 'Navegador web'}
                                                </td>

                                                {/* Tipo de acceso */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${loginCfg.bgColor} ${loginCfg.textColor}`}>
                                                        <loginCfg.Icon className="h-3 w-3" />
                                                        {loginCfg.label}
                                                    </span>
                                                </td>

                                                {/* Resultado — NUEVA columna clave */}
                                                <td className="px-4 py-3">
                                                    {isSuccess ? (
                                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                                            <CheckCircle2 className="h-3 w-3" />
                                                            Exitoso
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                                                            <XCircle className="h-3 w-3" />
                                                            Fallido
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Fecha */}
                                                <td className="px-4 py-3 text-sm text-slate-500">
                                                    {formatDate(log.logged_in_at)}
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <LogIn className="mx-auto mb-3 h-10 w-10 text-slate-200" />
                                            <p className="text-sm font-medium text-slate-500">No se encontraron registros</p>
                                            <p className="mt-1 text-xs text-slate-400">Intenta ajustar los filtros de búsqueda</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {pagination.total > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            )}
        </div>
    )
}
